// İl and ilçe as marketplaces, sellers and buyers type them: "İSTANBUL", "istanbul", "Istanbul" and
// " İstanbul " are one city. They are what an order keeps of its address once the buyer's personal
// data is erased (KVKK), so the regional figures of Performans count by them.

/** What an address without a city (or a district) reads as. */
export const PLACE_UNKNOWN = 'Bilinmiyor';

/**
 * The comparison key folds every upper-case letter to lower case the Turkish way, then the Turkish
 * letters to their plain ones (İ, I, ı, i → i; Ş, ş → s …), so a name typed without Turkish letters
 * ("Sisli") meets the right one ("Şişli"). The dot left over from a wrongly lower-cased İ ("i\u0307") goes.
 * PostgreSQL's translate() takes the same two strings (the dot has no counterpart: it is deleted).
 */
export const PLACE_FOLD_FROM = 'ABCÇDEFGĞHIİJKLMNOÖPQRSŞTUÜVWXYZÂÎÛçğıöşüâîû\u0307';
export const PLACE_FOLD_TO = 'abccdefgghiijklmnoopqrsstuuvwxyzaiucgiosuaiu';
/** Runs of spaces, tabs, line breaks and no-break spaces become one space (a RegExp and a PostgreSQL pattern alike). */
export const PLACE_SPACES = '[ \\t\\n\\r\\f\\v\\u00a0]+';

const SPACES = new RegExp(PLACE_SPACES, 'g');
const FOLD = new Map([...PLACE_FOLD_FROM].map((ch, i) => [ch, PLACE_FOLD_TO[i] ?? '']));
const EDGE_SPACE = /^ | $/g;
/** Letters only Turkish (and its circumflexes) has: a name spelled with them was typed with care. */
const TURKISH_LETTERS = /[çğıöşüâîûÇĞİÖŞÜÂÎÛ]/g;

const collapse = (raw: string | null | undefined) => (raw ?? '').replace(SPACES, ' ').replace(EDGE_SPACE, '');

/** The key places are grouped by: "  İSTANBUL " → "istanbul", "Şişli" → "sisli"; '' when there is no name. */
export function placeKey(raw: string | null | undefined): string {
  let key = '';
  for (const ch of collapse(raw)) key += FOLD.has(ch) ? FOLD.get(ch)! : ch;
  return key;
}

/** A name in Turkish title case: "KADIKÖY" → "Kadıköy", "istanbul" → "İstanbul"; no name → "Bilinmiyor". */
export function placeName(raw: string | null | undefined): string {
  const clean = collapse((raw ?? '').normalize('NFC').replace(/\u0307/g, ''));
  if (!clean) return PLACE_UNKNOWN;
  return clean
    .toLocaleLowerCase('tr-TR')
    .replace(/(^|[ \-\/(.])(\p{L})/gu, (_, before: string, letter: string) => before + letter.toLocaleUpperCase('tr-TR'));
}

/**
 * The name a group of spellings of one place is shown by: the one with the most Turkish letters
 * ("Şişli" over "Sisli"), then one not written in capitals with a plain I (which may stand for İ),
 * then the most used one.
 */
export function pickPlaceName(spellings: readonly { readonly name: string | null; readonly orders: number }[]): string {
  const ranked = new Map<string, { turkish: number; plainCapitalI: boolean; orders: number }>();
  for (const s of spellings) {
    const raw = collapse(s.name);
    if (!raw) continue;
    const name = placeName(raw);
    const turkish = raw.match(TURKISH_LETTERS)?.length ?? 0;
    const plainCapitalI = /I/.test(raw) && raw === raw.toLocaleUpperCase('tr-TR');
    const seen = ranked.get(name);
    if (!seen) ranked.set(name, { turkish, plainCapitalI, orders: s.orders });
    else ranked.set(name, { turkish: Math.max(seen.turkish, turkish), plainCapitalI: seen.plainCapitalI && plainCapitalI, orders: seen.orders + s.orders });
  }
  const best = [...ranked.entries()].sort(
    ([a, x], [b, y]) => y.turkish - x.turkish || Number(x.plainCapitalI) - Number(y.plainCapitalI) || y.orders - x.orders || a.localeCompare(b, 'tr'),
  )[0];
  return best ? best[0] : PLACE_UNKNOWN;
}
